// components/slides/brandResearchCommands.ts
import type { SlideBrandResearchRun, SlideBrandResearch, UiCommandDefinition } from '#types/types';
import { useSlidesStore } from './slidesStore';
import { researchWebsiteHost } from '#lib/slides/brandResearch';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { fetchApi } from '@/kernel/apiFetch';
import { createWebSearchHandler } from '#lib/webSearchCommand';

const searchWeb = createWebSearchHandler('SLIDES_WEB_SEARCH', 'Slides', { retryScope: 'research run' });
const requireRun = (sessionId?: string): SlideBrandResearchRun => {
  const st = useSlidesStore.getState();
  const run = st.brandResearchRun;
  if (!run || run.controller.signal.aborted || run.projectId !== st.activeProjectId || run.sessionId !== sessionId) {
    throw new Error('Brand research was stopped or the deck changed. Ask for it again in the chat, or launch the review.');
  }
  return run;
};
/** The deck a conversation is about. A Slides conversation is born for its deck (creation scope), and a
 *  fresh one has no subject yet when the review's brand research starts, so the scope counts too. The
 *  shared resolver leaves Slides scopes out on purpose (Studio shortcuts must not arm on a deck). */
const deckOfSession = (session: { creationModule?: unknown; creationEntityType?: unknown; creationEntityId?: unknown } | null | undefined) =>
  resolveProjectIdFromChatSessionContext(session)
  ?? (session?.creationModule === 'slides' && session.creationEntityType === 'Project' && typeof session.creationEntityId === 'string' ? session.creationEntityId : null);

const assertCurrent = (run: SlideBrandResearchRun) => {
  if (requireRun(run.sessionId).id !== run.id) throw new Error('This brand research was replaced by a newer run.');
};
const patch = (run: SlideBrandResearchRun, data: Partial<SlideBrandResearchRun>) => {
  assertCurrent(run);
  useSlidesStore.getState().patchBrandResearch(run.id, data);
};

export const SLIDES_RESEARCH_COMMANDS: Record<string, UiCommandDefinition> = {
  SLIDES_START_BRAND_RESEARCH: {
    action_type: 'SLIDES_START_BRAND_RESEARCH', targetEntityType: 'ABSOLUTE', availableInModes: ['slides'], headlessCompatible: true,
    description: 'Start brand research when the user asks in chat to research the brand or its latest concept. Call before SLIDES_IDENTIFY_BRAND and the research tools. Research may spend credits; never start it without a user request. Existing saved research remains unchanged until a new brief is saved.',
    parameters: [],
    handler: async (params, kernel, options) => {
      const st = useSlidesStore.getState();
      const sessionId = options?.sessionId;
      const session = sessionId ? kernel.current?.getInventory().ChatSession.registry[sessionId] : null;
      const projectId = deckOfSession(session);
      if (!sessionId || !projectId || projectId !== st.activeProjectId) throw new Error('Open this conversation’s deck before researching its brand.');
      if (st.brandResearchRun) requireRun(sessionId);
      else st.beginBrandResearch(projectId, sessionId);
      params.result = { started: true, next: 'Identify the brand, then research the web before internal context.' };
    },
  },
  SLIDES_END_BRAND_RESEARCH: {
    action_type: 'SLIDES_END_BRAND_RESEARCH', targetEntityType: 'ABSOLUTE', availableInModes: ['slides'], headlessCompatible: true,
    description: 'End unfinished research only when the user explicitly asks to stop/skip it or move on to reviewing. Use this command alone in its script, then continue the user’s requested task on the next turn. Do not use it for a brand clarification or to bypass the research sequence. Keeps previously saved research and discards only the unsaved research run.',
    parameters: [],
    handler: async (params, kernel, options) => {
      const run = requireRun(options?.sessionId);
      // This command runs after prior research commands settle. Release the mode
      // without aborting the current turn that must continue the user's new task.
      useSlidesStore.setState({ brandResearchRun: null });
      kernel.current?.patchEntityRuntimeState('ChatSession', run.sessionId, { slidesButtonIntent: null });
      params.result = { ended: true, next: 'Continue the user’s latest request with the normal Slides tools.' };
    },
  },
  SLIDES_IDENTIFY_BRAND: {
    action_type: 'SLIDES_IDENTIFY_BRAND', targetEntityType: 'ABSOLUTE', availableInModes: ['slides'], headlessCompatible: true,
    traceLabels: { active: 'identifying the brand', done: 'identified the brand', unit: 'brand' },
    description: 'Set the exact brand identified from this deck or the user. Ask if uncertain. Call FIRST during brand research and immediately when the user corrects the brand, even outside research: old research is then invalidated. Never guess from a similar registry name.',
    parameters: [
      { name: 'brandName', type: 'string', required: true, description: 'Exact retail brand name, not the mall or landlord.' },
      { name: 'websiteUrl', type: 'string', required: false, description: 'Official brand website only when known.' },
    ],
    handler: async (params, _kernel, options) => {
      const name = typeof params.brandName === 'string' ? params.brandName.trim() : '';
      if (!name || name.length > 200) throw new Error('A clear brand name is required.');
      const websiteUrl = typeof params.websiteUrl === 'string' ? params.websiteUrl.trim() : undefined;
      const st = useSlidesStore.getState();
      const run = st.brandResearchRun;
      const session = options?.sessionId ? _kernel?.current?.getInventory().ChatSession.registry[options.sessionId] : null;
      const projectId = run ? requireRun(options?.sessionId).projectId : deckOfSession(session);
      if (projectId !== st.activeProjectId) throw new Error('The deck changed before the brand correction completed.');
      if (!projectId) throw new Error('Open a deck first.');
      const old = st.decks[projectId]?.doc.brandResearch;
      const changedBrand = old && (old.brand.name.toLowerCase() !== name.toLowerCase() ||
        (!!websiteUrl && researchWebsiteHost(old.brand.websiteUrl) !== researchWebsiteHost(websiteUrl)));
      if (old && (changedBrand || old.status === 'invalidated')) {
        const invalidated: SlideBrandResearch = {
          brand: { name, websiteUrl }, researchedAt: new Date().toISOString(), status: 'invalidated', brief: '', sources: [], references: [],
        };
        st.applyDocMutation(projectId, (doc) => ({ ...doc, brandResearch: invalidated }));
        await st.saveDeckNow(projectId);
        const latest = useSlidesStore.getState();
        if (latest.saveStates[projectId]?.status === 'error' || latest.decks[projectId]?.doc.brandResearch?.researchedAt !== invalidated.researchedAt) {
          const current = latest.decks[projectId]?.doc.brandResearch;
          if (!current || current.researchedAt === old.researchedAt || current.researchedAt === invalidated.researchedAt) {
            latest.applyDocMutation(projectId, (doc) => ({ ...doc, brandResearch: invalidated }));
          }
          throw new Error('The brand correction could not be saved. Previous research is invalid; retry the correction before reviewing.');
        }
      }
      if (run) {
        assertCurrent(run);
        if (run.brand?.name.toLowerCase() !== name.toLowerCase() || (websiteUrl && researchWebsiteHost(run.brand?.websiteUrl) !== researchWebsiteHost(websiteUrl))) patch(run, {
          brand: { name, websiteUrl }, phase: 'web', searches: 0, sources: [], references: [], webFailed: false, internalFailed: false,
        });
        else patch(run, { brand: { name, websiteUrl: websiteUrl ?? run.brand.websiteUrl } });
      }
      params.result = { brandName: name, websiteUrl };
    },
  },
  SLIDES_WEB_SEARCH: {
      action_type: 'SLIDES_WEB_SEARCH',
      traceLabels: { active: 'searching', done: 'searched', unit: 'query', pluralUnit: 'queries' },
      targetEntityType: 'ABSOLUTE',
      description:
        'Search the web during brand research. For a research request typed in chat, call SLIDES_START_BRAND_RESEARCH and SLIDES_IDENTIFY_BRAND first. Read the full text of results and cite each claim inline with its source URL. Refine within the three-search budget, then continue with internal context and save the cited brief.',
      parameters: [
        { name: 'query', type: 'string', description: 'The search query.', required: true },
        {
          name: 'freshness',
          type: 'string',
          description: 'Optional. Time-scopes results.',
          required: false,
          options: ['oneDay', 'oneWeek', 'oneMonth', 'oneYear'],
        },
        {
          name: 'summary',
          type: 'boolean',
          description: 'Optional. If true, requests a summary of the search results. Defaults to false.',
          required: false,
        },
      ],
      availableInModes: ['slides'],
      headlessCompatible: true,
      handler: async (params, kernel, options) => {
        const run = requireRun(options?.sessionId);
        if (!run.brand || run.phase !== 'web') throw new Error('Identify the brand first. Complete all web searches before reading internal context.');
        if (run.searches >= 3) throw new Error('Three-search budget reached. Continue with internal brand context.');
        patch(run, { searches: run.searches + 1 });
        try {
          const searchParams = { ...params };
          await searchWeb(searchParams, kernel, { ...options, abortSignal: run.controller.signal });
          assertCurrent(run);
          params.results = searchParams.results;
          const results = searchParams.results;
          const rawResults = Array.isArray(results?.results) ? results.results : [];
          const current = requireRun(options?.sessionId);
          patch(run, {
            webFailed: current.webFailed || results?.provider === 'none',
            sources: [...current.sources, ...rawResults.map((entry: any) => ({
              url: entry.url, title: entry.title ?? entry.url, publishedDate: entry.publishedDate,
              kind: 'web' as const, image: entry.image,
            }))].filter((entry, i, all) => entry.url && all.findIndex((other) => other.url === entry.url) === i),
          });
        } catch (error) {
          assertCurrent(run);
          patch(run, { webFailed: true });
          params.results = { provider: 'none', query: params.query, results: [], notice: `Web search failed: ${error instanceof Error ? error.message : String(error)}. Continue with internal data and mark research partial.` };
        }
      },
    },

  SLIDES_READ_BRAND_CONTEXT: {
    action_type: 'SLIDES_READ_BRAND_CONTEXT', targetEntityType: 'ABSOLUTE', availableInModes: ['slides'], headlessCompatible: true,
    traceLabels: { active: 'checking Fater brand knowledge', done: 'checked Fater brand knowledge', unit: 'brand' },
    description: 'After ALL web searches, read additional brand information and verified facts directly from Fater’s shared database. No records are changed. Explain mismatches or ambiguity; do not guess or call Source.',
    parameters: [],
    handler: async (params, _kernel, options) => {
      const run = requireRun(options?.sessionId);
      if (!run.brand || run.searches === 0 || run.phase !== 'web') throw new Error('Finish web research first.');
      patch(run, { phase: 'internal' });
      try {
        const res = await fetchApi('/api/slides/brand-context', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: run.projectId, brandName: run.brand.name, websiteUrl: run.brand.websiteUrl }), signal: run.controller.signal });
        if (!res.ok) throw new Error(`Internal lookup failed (${res.status})`);
        const result = await res.json();
        assertCurrent(run);
        params.result = result;
        const facts = Array.isArray(result.facts) ? result.facts : [];
        patch(run, { sources: [...requireRun(options?.sessionId).sources, ...facts.filter((f: any) => f.sourceUrl).map((f: any) => ({
          url: f.sourceUrl, title: String(f.content).slice(0, 200), publishedDate: f.verifiedAt, kind: 'internal' as const,
        }))], internalFailed: result.status === 'ambiguous' });
      } catch (error) {
        assertCurrent(run);
        patch(run, { internalFailed: true });
        params.result = { status: 'failed', message: String(error), facts: [] };
      }
    },
  },
  SLIDES_COLLECT_BRAND_REFERENCES: {
    action_type: 'SLIDES_COLLECT_BRAND_REFERENCES', targetEntityType: 'ABSOLUTE', availableInModes: ['slides'], headlessCompatible: true,
    traceLabels: { active: 'checking visual references', done: 'checked visual references', unit: 'brand' },
    description: 'After the internal lookup, select the strongest dated store-concept pages from your search results, inspect their actual images, and save up to three relevant references. Prefer articles showing interiors or storefronts over brand landing pages. Logos, product shots and unrelated stores are rejected. Call even when search results contain no thumbnail: images can be discovered on the source pages.',
    parameters: [{ name: 'sourceUrls', type: 'array', required: false, description: 'Up to six source URLs from these web results, ordered by relevance to the current retail concept. Prioritize pages showing the store, materials, lighting and signage.' }],
    handler: async (params, _kernel, options) => {
      const run = requireRun(options?.sessionId);
      if (!run.brand || run.phase !== 'internal') throw new Error('Read internal brand context first.');
      const webSources = run.sources.filter((s) => s.kind === 'web');
      const selected = Array.isArray(params.sourceUrls) ? params.sourceUrls.slice(0, 6).map((url) => webSources.find((s) => s.url === url)).filter((s): s is typeof webSources[number] => !!s) : webSources.slice(0, 6);
      const candidates = selected.map((s) => ({ imageUrl: s.image, sourceUrl: s.url, title: s.title, publishedDate: s.publishedDate }));
      try {
        const res = candidates.length ? await fetchApi('/api/slides/brand-references', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: run.projectId, brandName: run.brand.name, candidates }), signal: run.controller.signal }) : null;
        if (res && !res.ok) throw new Error(`Reference inspection failed (${res.status})`);
        const result = res ? await res.json() : { references: [], notice: 'No source pages were returned by web search.' };
        assertCurrent(run);
        patch(run, { references: result.references ?? [], phase: 'ready' });
        params.result = result;
      } catch (error) {
        assertCurrent(run);
        patch(run, { references: [], phase: 'ready', referencesFailed: true });
        params.result = { references: [], notice: String(error) };
      }
    },
  },
  SLIDES_SAVE_BRAND_RESEARCH: {
    action_type: 'SLIDES_SAVE_BRAND_RESEARCH', targetEntityType: 'ABSOLUTE', availableInModes: ['slides'], headlessCompatible: true,
    traceLabels: { active: 'saving brand research', done: 'saved brand research', unit: 'brief' },
    description: 'Save the completed research brief with inline source links, source dates/markets, current retail concept, brand identity, materials, signage and lighting. Distinguish web evidence, internal knowledge and uncertainty. Guidelines outrank inspiration. After saving, report the brief and each vetted image in chat; never annotate or launch a review from research. When the result says a review follows, keep that report to two or three lines: the app starts the review itself. Otherwise wait for Launch review.',
    parameters: [{ name: 'brief', type: 'string', required: true, description: 'Concise cited brief in the user’s language. Qualify latest-concept claims when official dated evidence is missing.' }],
    handler: async (params, _kernel, options) => {
      const run = requireRun(options?.sessionId);
      if (!run.brand || run.phase !== 'ready') throw new Error('Finish the web, internal and reference steps first.');
      const brief = typeof params.brief === 'string' ? params.brief.trim() : '';
      if (!brief || brief.length > 12000) throw new Error('A concise research brief is required (maximum 12,000 characters).');
      const st = useSlidesStore.getState();
      const old = st.decks[run.projectId]?.doc.brandResearch;
      if ((run.webFailed || run.internalFailed || run.referencesFailed || (old?.references.length && !run.references.length)) && old && old.status !== 'invalidated') {
        patch(run, { phase: 'complete' });
        params.result = { notice: 'Refresh failed; the previous valid research was preserved.', research: old };
        return;
      }
      const research: SlideBrandResearch = {
        brand: run.brand, researchedAt: new Date().toISOString(),
        status: run.webFailed || run.internalFailed || !run.references.length || !run.sources.some((s) => s.kind === 'web') ? 'partial' : 'complete',
        brief, sources: run.sources.map(({ image: _image, ...source }) => source), references: run.references,
      };
      assertCurrent(run);
      st.applyDocMutation(run.projectId, (doc) => ({ ...doc, brandResearch: research }));
      await st.saveDeckNow(run.projectId);
      const saved = useSlidesStore.getState();
      const cancelled = run.controller.signal.aborted || saved.brandResearchRun?.id !== run.id;
      if (cancelled || saved.saveStates[run.projectId]?.status === 'error' || saved.decks[run.projectId]?.doc.brandResearch?.researchedAt !== research.researchedAt) {
        // Roll back only our own draft, including a cancellation while PUT was pending.
        if (saved.decks[run.projectId]?.doc.brandResearch?.researchedAt === research.researchedAt) {
          saved.applyDocMutation(run.projectId, (doc) => ({ ...doc, brandResearch: old }));
          await saved.saveDeckNow(run.projectId);
        }
        throw new Error(cancelled ? 'Brand research was cancelled.' : 'Brand research could not be saved. Retry when the connection is restored.');
      }
      assertCurrent(run);
      patch(run, { phase: 'complete' });
      params.result = { research, reviewFollows: !!run.thenReview, instruction: run.thenReview
        ? 'Research is finished and the review starts automatically next: summarise the brief in two or three lines.'
        : 'Research is finished. Present this brief and its reference images; then wait for Launch review.' };
    },
  },
};
